import React from 'react';
import { MANUFACTURER_SERVICES_LOCATIONS, TERMS_OF_USE_LOCATIONS, TERMS_OF_USE_TABS } from '../config/routerLocations';
import { ObjectKeyValueString } from '../helpers/types';
import { AVAILABLE_CONTENT_LANG, getDefaultContentKey } from '../i18n/content';
import { LocalizationContext } from './localizationContext';
import { PlatformContext } from './platformContext';

type LinkType = 'dealers' | 'default' | 'us' | 'subusers';

interface DxfIntegrationState {
  isLoaded: boolean;
  termsOfUseLink: string;
  termsOfUseForSubUsersLink: string;
  contentCardCreatorLink: string;
  skidTermsOfUseLink: string;
  isDPASupported: boolean;
  jumpToPage: (targetLocation: string) => void;
  getInternalNavigationLink: (linkType: LinkType) => string;
}

interface DxfIntegrationContextProps extends DxfIntegrationState {
}

interface DxfIntegrationContextProviderProps {
}

const supportedDPAPageCountries: string[] = [
  'AT', // "Austria"
  'DE', // "Germany"
  'EN', // "Rest of Europe and African Countries"
  'GB', // "United Kingdom" - DEFAULT
  'ES', // "Spain"
  'FR', // "France"
  'LU', // "Luxembourg"
  'IT', // "Italy"
  'BE', // "Belgium"
  'NL', // "Netherlands"
  // 'PS', // "Rest of the World" ... Don't show AVV/DPA for the current scope
];

const internalNavigationLinkMap = {
  dealers: {
    dxf: '/en/en/terms-of-use/terms-of-use-for-dealers-sub-portal.html',
    default: '/terms-of-use-for-dealers-sub-portal?lang=en-GB',
  },
  default: {
    dxf: '/en/en/terms-of-use',
    default: '/terms-of-use?lang=en-GB',
  },
  us: {
    dxf: '/us/en/terms-of-use',
    default: '/terms-of-use-us?lang=en-US',
  },
  subusers: {
    dxf: '/en/en/terms-of-use/terms-of-use-for-subusers.html',
    default: '/terms-of-use-for-sub-users?lang=en-GB',
  },
};

const defaultState: DxfIntegrationState = {
  isLoaded: false,
  termsOfUseLink: '',
  termsOfUseForSubUsersLink: '',
  contentCardCreatorLink: '',
  skidTermsOfUseLink: '',
  isDPASupported: false,
  jumpToPage: () => { },
  getInternalNavigationLink: () => '',
};

const unsupportedSKIDLocales = ['en-TW'];

const validateSKIDLocale = (input: string): string => {
  const contentLangKey = input.split('-').join('_');
  if (input.length && (unsupportedSKIDLocales.includes(input) || !AVAILABLE_CONTENT_LANG[contentLangKey])) {
    return getDefaultContentKey(input);
  }

  return input;
};

export const DXF_LOCATION: ObjectKeyValueString = Object.freeze({
  TERMS_OF_USE: `/terms-of-use?tab=${TERMS_OF_USE_TABS.TERMS_OF_USE}`,
  TERMS_OF_USE_FOR_SUB_USERS: '/terms-of-use/terms-of-use-for-subusers.html',
  TERMS_OF_USE_FOR_DEALERS_SUB_PORTAL: '/terms-of-use/terms-of-use-for-dealers-sub-portal.html',
  TERMS_OF_USE_FOR_MANUFACTURER_SERVICES: '/terms-of-use/terms-of-use-for-manufacturer-services.html',
  DATA_PROCESSING_AGREEMENT: `/terms-of-use?tab=${TERMS_OF_USE_TABS.DATA_PROCESSING_AGREEMENT}`,
});

export const LOCATION_MAPS: ObjectKeyValueString = Object.freeze({
  /* gooseValue: dxfValue */
  [TERMS_OF_USE_LOCATIONS.TERMS_OF_USE]: DXF_LOCATION.TERMS_OF_USE,
  [TERMS_OF_USE_LOCATIONS.TERMS_OF_USE_FOR_SUB_USERS]: DXF_LOCATION.TERMS_OF_USE_FOR_SUB_USERS,
  [TERMS_OF_USE_LOCATIONS.TERMS_OF_USE_FOR_DEALERS_SUB_PORTAL]: DXF_LOCATION.TERMS_OF_USE_FOR_DEALERS_SUB_PORTAL,
  [TERMS_OF_USE_LOCATIONS.TERMS_OF_USE_FOR_MANUFACTURER_SERVICES]: DXF_LOCATION.TERMS_OF_USE_FOR_MANUFACTURER_SERVICES,
  [TERMS_OF_USE_LOCATIONS.DATA_PROCESSING_AGREEMENT]: DXF_LOCATION.DATA_PROCESSING_AGREEMENT,
});

// This is hack to remove dangling FirstSpirit element that is empty
// but still effects the page layout
export const dxfStyleFix = () => {
  const el = document.getElementsByClassName('M-Stage-Two')[0];
  if (el?.getAttribute('data-js-component') === 'stage-two') {
    el.className = `${el.className}-tou`;
  }
};

export const DxfIntegrationContext = React.createContext<DxfIntegrationContextProps>(defaultState);

export const DxfIntegrationContextProvider: React.FunctionComponent<React.PropsWithChildren<DxfIntegrationContextProviderProps>> = ({ children }) => {
  const platformContext = React.useContext(PlatformContext);
  const localizationContext = React.useContext(LocalizationContext);
  const [dxfIntegrationState, setDxfIntegrationState] = React.useState<DxfIntegrationState>(defaultState);

  const getDXFLink = (targetLocation: string): string => (
    platformContext.isDXF ? `${localizationContext.colang}${LOCATION_MAPS[targetLocation]}` : targetLocation
  );

  const jumpToPage = (targetLocation: string): void => {
    const normalizedLocation = getDXFLink(targetLocation);

    window.location.href = normalizedLocation;
  };

  const getInternalNavigationLink = (linkType: LinkType): string => (
    platformContext.isDXF ? internalNavigationLinkMap[linkType].dxf : internalNavigationLinkMap[linkType].default
  );

  React.useEffect(() => {
    if (!platformContext.isLoaded || !localizationContext.isLoaded) {
      return;
    }

    setDxfIntegrationState({
      ...dxfIntegrationState,
      isLoaded: true,
      termsOfUseLink: getDXFLink(TERMS_OF_USE_LOCATIONS.TERMS_OF_USE),
      termsOfUseForSubUsersLink: getDXFLink(TERMS_OF_USE_LOCATIONS.TERMS_OF_USE_FOR_SUB_USERS),
      contentCardCreatorLink: `${getDXFLink(TERMS_OF_USE_LOCATIONS.TERMS_OF_USE_FOR_MANUFACTURER_SERVICES)}?tab=${MANUFACTURER_SERVICES_LOCATIONS.CONTENT_CARD_CREATOR}`,
      skidTermsOfUseLink: `https://singlekey-id.com/auth/${validateSKIDLocale(localizationContext.locale)?.toLocaleLowerCase()}/terms-of-use`,
      isDPASupported: supportedDPAPageCountries.includes(localizationContext.country?.toUpperCase()),
      jumpToPage,
      getInternalNavigationLink,
    });
  }, [platformContext, localizationContext]);

  return (
    <DxfIntegrationContext.Provider value={dxfIntegrationState}>
      {children}
    </DxfIntegrationContext.Provider>
  );
};
